import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, Shield, Lock, Zap, Droplets, Crown, Gift } from 'lucide-react';
type OfferKey = 'standard' | 'elite';
const OFFERS: Record<OfferKey, {
  name: string;
  price: string;
  oldPrice: string;
  url: string;
}> = {
  standard: {
    name: 'El Manual del 5 Moderno',
    price: '$19.990',
    oldPrice: '$52.690',
    url: 'https://checkout.ebookpoint.shop/cart/59546532511825:1'
  },
  elite: {
    name: 'El Manual del 5 Moderno ELITE',
    price: '$27.990',
    oldPrice: '$72.690',
    url: 'https://checkout.ebookpoint.shop/cart/59556322410577:1'
  }
};
export function Pricing() {
  const [selected, setSelected] = useState<OfferKey>('standard');
  const current = OFFERS[selected];
  return <section id="checkout" className="py-12 px-4 bg-brand-surface border-t border-brand-border overflow-hidden" data-id="element-112">
      <div className="max-w-4xl mx-auto" data-id="element-113">
        <div className="text-center mb-8" data-id="element-114">
          <h2 className="font-display text-4xl md:text-6xl tracking-wide text-brand-text" data-id="element-115">
            ACCEDÉ AL MANUAL <span className="text-brand-green" data-id="element-116">COMPLETO</span>{' '}
            HOY
          </h2>
        </div>

        {/* ELITE bonus teaser callout */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5
      }} className="mb-6 relative bg-gradient-to-br from-[#0F172A] via-[#1E293B] to-[#0F172A] text-white rounded-2xl p-5 md:p-6 flex items-center gap-4 shadow-xl border border-yellow-400/40 overflow-hidden" data-id="element-117">
          <div className="absolute -top-10 -right-10 w-40 h-40 bg-yellow-400/10 rounded-full blur-3xl pointer-events-none" data-id="element-118"></div>
          <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-yellow-400/5 rounded-full blur-3xl pointer-events-none" data-id="element-119"></div>

          <div className="flex-shrink-0 w-14 h-14 md:w-16 md:h-16 rounded-full bg-gradient-to-br from-yellow-300 to-yellow-500 flex items-center justify-center shadow-lg shadow-yellow-500/40" data-id="element-120">
            <Crown className="w-7 h-7 md:w-8 md:h-8 text-[#0F172A]" strokeWidth={2.5} data-id="element-121" />
          </div>

          <div className="flex-1 relative z-10" data-id="element-122">
            <div className="inline-block bg-yellow-400 text-[#0F172A] font-bold text-[10px] uppercase px-2 py-0.5 rounded-md tracking-wider mb-1" data-id="element-123">
              Bono ELITE
            </div>
            <p className="font-display text-xl md:text-2xl leading-tight tracking-wide" data-id="element-124">
              ELEGÍ <span className="text-yellow-400" data-id="element-125">ELITE</span> Y SUMÁ EL
              SISTEMA DE PASES Y TIROS DEL 5 MODERNO
            </p>
            <p className="text-sm md:text-base text-white/80 mt-1 leading-snug" data-id="element-126">
              Bonos premium exclusivos para llevar tu juego al siguiente nivel.
            </p>
          </div>
        </motion.div>

        {/* Mobile-only proteins callout */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="md:hidden mb-6 bg-white border border-brand-green/40 rounded-xl flex flex-col relative overflow-hidden shadow-lg" data-id="element-127">
          <div className="absolute top-2 right-2 z-20 bg-yellow-400 text-brand-text font-bold text-[10px] uppercase px-2 py-1 rounded-md shadow-md tracking-wider" data-id="element-128">
            BONUS EXTRA
          </div>
          <div className="relative w-full h-44 overflow-hidden bg-brand-surface" data-id="element-129">
            <img src="https://cdn.magicpatterns.com/uploads/csfEynJovmbs3Pwd8Wscsu/image.png" alt="Vista previa de la calculadora de proteínas diarias" className="w-full h-full object-cover object-top" data-id="element-130" />
            <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-b from-transparent via-white/70 to-white pointer-events-none" data-id="element-131"></div>
          </div>
          <div className="px-5 pb-5 pt-1 -mt-8 relative z-10" data-id="element-132">
            <div className="flex items-start gap-2 mb-1" data-id="element-133">
              <Droplets className="w-6 h-6 flex-shrink-0 text-brand-green mt-0.5" data-id="element-134" />
              <p className="font-display text-xl leading-tight tracking-wide text-brand-text" data-id="element-135">
                QUE LOS PARTIDOS NO TE PESEN
              </p>
            </div>
            <p className="text-sm leading-snug text-brand-muted" data-id="element-136">
              Calculadora de{' '}
              <strong className="text-brand-green" data-id="element-137">
                proteínas e hidratación
              </strong>{' '}
              incluida comprando hoy
            </p>
          </div>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="bg-white border-2 border-brand-green rounded-2xl p-6 md:p-10 relative shadow-[0_20px_60px_rgba(22,163,74,0.15)]" data-id="element-138">
          {/* Desktop floating protein callout */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.8,
          rotate: -8
        }} whileInView={{
          opacity: 1,
          scale: 1,
          rotate: 6
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.3,
          type: 'spring',
          stiffness: 120
        }} className="hidden md:flex absolute -right-10 lg:-right-28 top-1/2 -translate-y-1/2 w-60 lg:w-72 bg-white border border-brand-green/40 rounded-2xl flex-col overflow-hidden shadow-2xl shadow-brand-green/40 z-10" data-id="element-139">
            <div className="absolute top-2 right-2 z-20 bg-yellow-400 text-brand-text font-bold text-[10px] uppercase px-2 py-1 rounded-md shadow-md tracking-wider" data-id="element-140">
              BONUS EXTRA
            </div>
            <div className="relative w-full h-40 lg:h-44 overflow-hidden bg-brand-surface" data-id="element-141">
              <img src="https://cdn.magicpatterns.com/uploads/csfEynJovmbs3Pwd8Wscsu/image.png" alt="Vista previa de la calculadora de proteínas diarias" className="w-full h-full object-cover object-top" data-id="element-142" />
              <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-b from-transparent via-white/70 to-white pointer-events-none" data-id="element-143"></div>
            </div>
            <div className="px-4 pb-4 pt-1 -mt-8 relative z-10" data-id="element-144">
              <div className="flex items-start gap-2 mb-1" data-id="element-145">
                <Droplets className="w-6 h-6 flex-shrink-0 text-brand-green" data-id="element-146" />
                <p className="font-display text-lg lg:text-xl leading-[0.95] tracking-wide text-brand-text" data-id="element-147">
                  QUE LOS PARTIDOS NO TE PESEN
                </p>
              </div>
              <p className="text-xs lg:text-sm leading-snug text-brand-muted" data-id="element-148">
                Calculadora de{' '}
                <strong className="text-brand-green" data-id="element-149">
                  proteínas e hidratación
                </strong>{' '}
                incluida comprando hoy
              </p>
            </div>
          </motion.div>

          <div className="absolute -top-5 left-1/2 -translate-x-1/2 bg-brand-green text-white font-bold px-6 py-2 rounded-full text-sm md:text-base uppercase tracking-wider whitespace-nowrap shadow-md" data-id="element-150">
            Elegí tu versión
          </div>

          {/* Two stacked compact offer cards (radio selection) */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4 mb-6 mt-2" data-id="element-151">
            {/* STANDARD */}
            <button type="button" onClick={() => setSelected('standard')} aria-pressed={selected === 'standard'} className={`text-left rounded-xl p-4 border-2 transition-all relative ${selected === 'standard' ? 'border-brand-green bg-brand-greenSoft shadow-md shadow-brand-green/20' : 'border-brand-border bg-white hover:border-brand-green/50'}`} data-id="element-152">
              <div className="flex items-start gap-3" data-id="element-153">
                <div className={`flex-shrink-0 w-5 h-5 rounded-full border-2 mt-1 flex items-center justify-center ${selected === 'standard' ? 'border-brand-green bg-brand-green' : 'border-brand-border'}`} data-id="element-154">
                  {selected === 'standard' && <div className="w-2 h-2 rounded-full bg-white" data-id="element-155"></div>}
                </div>
                <div className="flex-1" data-id="element-156">
                  <div className="font-bold text-brand-text text-sm md:text-base leading-tight" data-id="element-157">
                    El Manual del 5 Moderno
                  </div>
                  <div className="flex items-baseline gap-2 mt-1" data-id="element-158">
                    <span className="text-brand-muted text-xs line-through" data-id="element-159">
                      $52.690
                    </span>
                    <span className="font-display text-2xl md:text-3xl text-brand-green tracking-wider" data-id="element-160">
                      $19.990
                    </span>
                  </div>
                  <div className="text-xs text-brand-muted mt-1" data-id="element-161">
                    5 capítulos + 5 bonos + 2 bonos extra
                  </div>
                </div>
              </div>
            </button>

            {/* ELITE */}
            <button type="button" onClick={() => setSelected('elite')} aria-pressed={selected === 'elite'} className={`text-left rounded-xl p-4 border-2 transition-all relative ${selected === 'elite' ? 'border-yellow-500 bg-yellow-50 shadow-md shadow-yellow-500/20' : 'border-brand-border bg-white hover:border-yellow-500/60'}`} data-id="element-162">
              <div className="absolute -top-2 right-3 bg-gradient-to-r from-yellow-400 to-yellow-500 text-[#0F172A] font-bold text-[10px] uppercase px-2 py-0.5 rounded-md shadow-sm tracking-wider flex items-center gap-1" data-id="element-163">
                <Crown className="w-3 h-3" strokeWidth={2.5} data-id="element-164" /> Más completo
              </div>
              <div className="flex items-start gap-3" data-id="element-165">
                <div className={`flex-shrink-0 w-5 h-5 rounded-full border-2 mt-1 flex items-center justify-center ${selected === 'elite' ? 'border-yellow-500 bg-yellow-500' : 'border-brand-border'}`} data-id="element-166">
                  {selected === 'elite' && <div className="w-2 h-2 rounded-full bg-white" data-id="element-167"></div>}
                </div>
                <div className="flex-1" data-id="element-168">
                  <div className="font-bold text-brand-text text-sm md:text-base leading-tight" data-id="element-169">
                    El Manual del 5 Moderno{' '}
                    <span className="text-yellow-600" data-id="element-170">ELITE</span>
                  </div>
                  <div className="flex items-baseline gap-2 mt-1" data-id="element-171">
                    <span className="text-brand-muted text-xs line-through" data-id="element-172">
                      $72.690
                    </span>
                    <span className="font-display text-2xl md:text-3xl text-yellow-600 tracking-wider" data-id="element-173">
                      $27.990
                    </span>
                  </div>
                  <div className="text-xs text-brand-text mt-2 space-y-0.5" data-id="element-174">
                    <div className="font-semibold" data-id="element-175">Incluye:</div>
                    <div className="flex items-start gap-1" data-id="element-176">
                      <Crown className="w-3 h-3 text-yellow-600 flex-shrink-0 mt-0.5" strokeWidth={2.5} data-id="element-177" />
                      <span data-id="element-178">
                        Bono ELITE: Sistema definitivo de pases del 5 moderno
                      </span>
                    </div>
                    <div className="flex items-start gap-1" data-id="element-179">
                      <Crown className="w-3 h-3 text-yellow-600 flex-shrink-0 mt-0.5" strokeWidth={2.5} data-id="element-180" />
                      <span data-id="element-181">
                        Bono ELITE: Sistema definitivo de tiros del 5 moderno
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </button>
          </div>

          {/* What's included summary + CTA */}
          <div className="grid md:grid-cols-2 gap-6 md:gap-8 items-center" data-id="element-182">
            <div data-id="element-183">
              <h3 className="text-lg md:text-xl font-bold mb-3 text-brand-text" data-id="element-184">
                Tu acceso incluye:
              </h3>
              <ul className="space-y-2 text-sm" data-id="element-185">
                <li className="flex items-start gap-2" data-id="element-186">
                  <CheckCircle className="w-5 h-5 text-brand-green flex-shrink-0 mt-0.5" data-id="element-187" />
                  <span className="text-brand-text" data-id="element-188">
                    <strong data-id="element-189">Manual del 5 Moderno</strong> (5 capítulos)
                  </span>
                </li>
                <li className="flex items-start gap-2" data-id="element-190">
                  <CheckCircle className="w-5 h-5 text-brand-green flex-shrink-0 mt-0.5" data-id="element-191" />
                  <span className="text-brand-text" data-id="element-192">
                    5 bonos gratis (Atlas, ejercicios, físico, análisis, guía)
                  </span>
                </li>
                <li className="flex items-start gap-2" data-id="element-193">
                  <CheckCircle className="w-5 h-5 text-brand-green flex-shrink-0 mt-0.5" data-id="element-194" />
                  <span className="text-brand-text" data-id="element-195">
                    Calculadora de proteínas e hidratación
                  </span>
                </li>
                <li className="flex items-start gap-2" data-id="element-196">
                  <CheckCircle className="w-5 h-5 text-brand-green flex-shrink-0 mt-0.5" data-id="element-197" />
                  <span className="text-brand-text" data-id="element-198">
                    <strong data-id="element-199">Bono EXTRA:</strong> Guía de decisiones, pases de
                    primer toque y agilidad
                  </span>
                </li>
                <li className="flex items-start gap-2" data-id="element-200">
                  <Gift className="w-5 h-5 text-brand-green flex-shrink-0 mt-0.5" data-id="element-201" />
                  <span className="text-brand-text" data-id="element-202">
                    <strong data-id="element-203">Bono de REGALO:</strong> Psicología Deportiva — Cómo
                    enfrentar las derrotas y las victorias
                  </span>
                </li>
                {selected === 'elite' && <>
                    <li className="flex items-start gap-2" data-id="element-204">
                      <Crown className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" strokeWidth={2.5} data-id="element-205" />
                      <span className="text-brand-text" data-id="element-206">
                        <strong data-id="element-207">Bono ELITE:</strong> Sistema de pases definitivo
                      </span>
                    </li>
                    <li className="flex items-start gap-2" data-id="element-208">
                      <Crown className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" strokeWidth={2.5} data-id="element-209" />
                      <span className="text-brand-text" data-id="element-210">
                        <strong data-id="element-211">Bono ELITE:</strong> Sistema de tiros definitivo
                      </span>
                    </li>
                  </>}
                <li className="flex items-start gap-2" data-id="element-212">
                  <CheckCircle className="w-5 h-5 text-brand-green flex-shrink-0 mt-0.5" data-id="element-213" />
                  <span className="text-brand-text" data-id="element-214">
                    <strong data-id="element-215">Bono:</strong> Las 10 formaciones más usadas y sus
                    tácticas
                  </span>
                </li>
                <li className="flex items-start gap-2" data-id="element-216">
                  <CheckCircle className="w-5 h-5 text-brand-green flex-shrink-0 mt-0.5" data-id="element-217" />
                  <span className="text-brand-text" data-id="element-218">
                    <strong data-id="element-219">Bono:</strong> Fuerza, estabilidad y prevención de
                    lesiones
                  </span>
                </li>
              </ul>
            </div>

            <div className="text-center bg-brand-surface p-5 md:p-6 rounded-xl border border-brand-border" data-id="element-220">
              <div className="text-brand-muted text-base line-through font-medium" data-id="element-221">
                {current.oldPrice}
              </div>
              <div className={`text-5xl md:text-6xl font-display mb-4 tracking-wider ${selected === 'elite' ? 'text-yellow-600' : 'text-brand-green'}`} data-id="element-222">
                {current.price}
              </div>

              <a href={current.url} className={`block w-full font-display text-2xl md:text-3xl py-4 px-6 rounded-lg uppercase tracking-wider transform transition-all hover:scale-105 active:scale-95 mb-4 shadow-lg text-white ${selected === 'elite' ? 'bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 shadow-yellow-500/40' : 'bg-brand-green hover:bg-brand-greenHover shadow-brand-green/30'}`} data-id="element-223">
                {selected === 'elite' ? 'QUIERO LA VERSIÓN ELITE' : 'QUIERO MI MANUAL AHORA'}
              </a>

              <div className="flex flex-wrap justify-center gap-3 text-xs text-brand-muted" data-id="element-224">
                <div className="flex items-center gap-1" data-id="element-225">
                  <Lock className="w-3.5 h-3.5" data-id="element-226" /> Pago seguro
                </div>
                <div className="flex items-center gap-1" data-id="element-227">
                  <Zap className="w-3.5 h-3.5" data-id="element-228" /> Acceso inmediato
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}